import React, { useEffect, useRef, useState } from 'react';
import config from '../config';
import PortfolioCvEditor, { createEmptyCv } from './PortfolioCvEditor';
import { AdminImageDropzone, dataTransferHasFiles, fetchAdminJson, getAdminImageUrl, uploadAdminImage } from './AdminImageCarousel';
import { VIDEO_ACCEPT, captureVideoPoster, uploadAdminVideo, validateVideoFile } from '../utils/adminVideo';
import { getProjectPosterUrl, getProjectVideoUrl } from '../utils/portfolioMedia';

const API_BASE = config.API_BASE;

const emptyGalleryItem = {
  image_url: '',
  title: '',
  client: '',
  year: ''
};

const emptyProject = {
  title: '',
  subtitle: '',
  description: '',
  body_text: '',
  media_type: 'image',
  media_url: '',
  thumbnail_url: '',
  slug: '',
  column: 'left',
  view_more_label: 'View..',
  metadata_client: '',
  metadata_role: '',
  metadata_year: '',
  gallery_items: [],
  display_order: 0,
  published: true
};

function authHeaders() {
  return {
    'Content-Type': 'application/json',
  };
}

function formatMediaUrl(url) {
  return getAdminImageUrl(url);
}

function getProjectGalleryDefaults(form) {
  return {
    title: form.title || '',
    client: form.metadata_client || '',
    year: form.metadata_year || ''
  };
}

function mergeGalleryItems(form, imageUrls) {
  const existingByUrl = new Map(
    (form.gallery_items || [])
      .filter((item) => item?.image_url)
      .map((item) => [item.image_url, item])
  );

  return imageUrls.map((url) => ({
    ...emptyGalleryItem,
    ...existingByUrl.get(url),
    image_url: url
  }));
}

function getGalleryImageUrls(form) {
  return (form.gallery_items || []).map((item) => item.image_url).filter(Boolean);
}

function resolveGalleryField(item, key, form) {
  const defaults = getProjectGalleryDefaults(form);
  const value = item?.[key];
  return value || defaults[key] || '';
}

function isUsingProjectDefault(item, key, form) {
  return !String(item?.[key] || '').trim();
}

function normalizeCvFromApi(cv) {
  if (cv?.profile && Array.isArray(cv.sections)) {
    return {
      profile: cv.profile,
      sections: cv.sections,
      lastUpdated: cv.lastUpdated || ''
    };
  }
  return createEmptyCv();
}

function buildProjectPayload(form) {
  const isVideo = form.media_type === 'video';
  return {
    title: form.title,
    subtitle: form.subtitle,
    description: form.description,
    body_text: form.body_text,
    media_type: form.media_type,
    media_url: form.media_url,
    thumbnail_url: isVideo ? getProjectPosterUrl(form) : form.media_url,
    slug: form.slug,
    column: form.column,
    view_more_label: form.view_more_label,
    metadata_client: form.metadata_client,
    metadata_role: form.metadata_role,
    metadata_year: form.metadata_year,
    gallery_items: Array.isArray(form.gallery_items) ? form.gallery_items : [],
    display_order: form.display_order,
    published: form.published !== false
  };
}

export default function PortfolioAdminSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [cv, setCv] = useState(createEmptyCv());
  const [cvSaving, setCvSaving] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const videoInputRef = useRef(null);

  const galleryItemsForMetadata = form.gallery_items || [];
  const galleryDefaults = getProjectGalleryDefaults(form);
  const mainImages = form.media_url ? [form.media_url] : [];
  const galleryImages = getGalleryImageUrls(form);
  const posterUrl = getProjectPosterUrl(form);
  const posterImages = posterUrl ? [posterUrl] : [];

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [projectsRes, cvRes] = await Promise.all([
        fetchAdminJson(`${API_BASE}/api/admin/portfolio/projects`),
        fetchAdminJson(`${API_BASE}/api/admin/portfolio/cv`)
      ]);
      if (!projectsRes.success) throw new Error(projectsRes.error || 'Failed to load projects');
      if (!cvRes.success) throw new Error(cvRes.error || 'Failed to load CV');
      setProjects(projectsRes.projects || []);
      setCv(normalizeCvFromApi(cvRes.cv));
    } catch (err) {
      setError(err.message || 'Failed to load portfolio admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyProject, gallery_items: [] });
    setShowForm(true);
  };

  const openEdit = (project) => {
    setEditingId(project._id || project.id);
    setForm({
      ...emptyProject,
      ...project,
      gallery_items: Array.isArray(project.gallery_items) ? project.gallery_items : []
    });
    setShowForm(true);
  };

  const updateGalleryItem = (index, key, value) => {
    setForm((prev) => {
      const gallery_items = [...(prev.gallery_items || [])];
      gallery_items[index] = { ...gallery_items[index], [key]: value };
      return { ...prev, gallery_items };
    });
  };

  const handleMainImageChange = (images) => {
    const main = images[0] || '';
    setForm((prev) => ({
      ...prev,
      media_url: main,
      thumbnail_url: main
    }));
  };

  const handleGalleryImagesChange = (images) => {
    setForm((prev) => ({
      ...prev,
      gallery_items: mergeGalleryItems(prev, images)
    }));
  };

  const handleMediaTypeChange = (mediaType) => {
    setForm((prev) => {
      if (prev.media_type === mediaType) return prev;
      if (mediaType === 'video') {
        // Keep the current image around as the video's still
        return { ...prev, media_type: 'video', media_url: '', thumbnail_url: prev.media_url || prev.thumbnail_url };
      }
      const still = getProjectPosterUrl(prev);
      return { ...prev, media_type: 'image', media_url: still, thumbnail_url: still };
    });
  };

  const handlePosterChange = (images) => {
    setForm((prev) => ({ ...prev, thumbnail_url: images[0] || '' }));
  };

  const handleVideoFile = async (file) => {
    if (!file || videoUploading) return;
    const invalid = validateVideoFile(file);
    if (invalid) {
      setError(invalid);
      return;
    }

    setError('');
    setVideoUploading(true);
    setVideoProgress(0);
    let posterFile = null;
    let warning = '';
    try {
      try {
        posterFile = await captureVideoPoster(file);
      } catch (err) {
        warning = err.message;
      }

      const videoUrl = await uploadAdminVideo(file, setVideoProgress);
      let newPosterUrl = '';
      if (posterFile) {
        try {
          newPosterUrl = await uploadAdminImage(posterFile);
        } catch (err) {
          warning = `Video uploaded, but saving the still image failed: ${err.message}`;
        }
      }

      setForm((prev) => ({
        ...prev,
        media_url: videoUrl,
        thumbnail_url: newPosterUrl || getProjectPosterUrl(prev)
      }));
      if (warning) setError(`${warning}. You can upload a still image manually below.`);
    } catch (err) {
      setError(err.message || 'Video upload failed');
    } finally {
      setVideoUploading(false);
    }
  };

  const handleVideoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleVideoFile(file);
  };

  const handleVideoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer && dataTransferHasFiles(e.dataTransfer)) e.dataTransfer.dropEffect = 'copy';
  };

  const saveProject = async (e) => {
    e.preventDefault();
    setError('');

    if (form.media_type === 'image' && !form.media_url) {
      setError('Add at least one project image.');
      return;
    }
    if (form.media_type === 'video' && !form.media_url) {
      setError('Upload a video (or paste a video URL) for video projects.');
      return;
    }
    if (videoUploading) {
      setError('Wait for the video upload to finish before saving.');
      return;
    }

    try {
      const isEdit = Boolean(editingId);
      const url = isEdit
        ? `${API_BASE}/api/admin/portfolio/projects/${editingId}`
        : `${API_BASE}/api/admin/portfolio/projects`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetchAdminJson(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(buildProjectPayload(form))
      });
      if (!res.success) throw new Error(res.error || 'Failed to save project');
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyProject, gallery_items: [] });
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to save project');
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    setError('');
    try {
      const res = await fetchAdminJson(`${API_BASE}/api/admin/portfolio/projects/${id}`, {
        method: 'DELETE'
      });
      if (!res.success) throw new Error(res.error || 'Failed to delete project');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete project');
    }
  };

  const saveCv = async () => {
    setCvSaving(true);
    setError('');
    try {
      const res = await fetchAdminJson(`${API_BASE}/api/admin/portfolio/cv`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(cv)
      });
      if (!res.success) throw new Error(res.error || 'Failed to save CV');
      setCv(normalizeCvFromApi(res.cv));
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to save CV');
    } finally {
      setCvSaving(false);
    }
  };

  return (
    <>
      <div className="admin-toolbar">
        <button type="button" className="admin-btn" onClick={openCreate}>
          Add Portfolio Project
        </button>
      </div>

      {loading && <div className="admin-message admin-message--loading">Loading…</div>}
      {error && <div className="admin-message admin-message--error">{error}</div>}

      {!loading && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginBottom: 18 }}>
          <thead>
            <tr style={{ background: '#f1f1f1' }}>
              <th style={{ border: '1px solid #ddd', padding: 8 }}>Preview</th>
              <th style={{ border: '1px solid #ddd', padding: 8 }}>Title</th>
              <th style={{ border: '1px solid #ddd', padding: 8 }}>Slug</th>
              <th style={{ border: '1px solid #ddd', padding: 8 }}>Column</th>
              <th style={{ border: '1px solid #ddd', padding: 8 }}>Order</th>
              <th style={{ border: '1px solid #ddd', padding: 8 }}>Published</th>
              <th style={{ border: '1px solid #ddd', padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project._id || project.id}>
                <td style={{ border: '1px solid #ddd', padding: 8 }}>
                  {project.media_type === 'video' ? (
                    getProjectPosterUrl(project) ? (
                      <img src={formatMediaUrl(getProjectPosterUrl(project))} alt={project.title} style={{ width: 80, height: 60, objectFit: 'cover' }} />
                    ) : (
                      <video src={formatMediaUrl(getProjectVideoUrl(project))} style={{ width: 80, height: 60, objectFit: 'cover' }} muted preload="metadata" />
                    )
                  ) : (
                    <img src={formatMediaUrl(project.thumbnail_url || project.media_url)} alt={project.title} style={{ width: 80, height: 60, objectFit: 'cover' }} />
                  )}
                </td>
                <td style={{ border: '1px solid #ddd', padding: 8 }}>{project.title}</td>
                <td style={{ border: '1px solid #ddd', padding: 8 }}>{project.slug}</td>
                <td style={{ border: '1px solid #ddd', padding: 8 }}>{project.column === 'right' ? 'Physical' : 'Digital'}</td>
                <td style={{ border: '1px solid #ddd', padding: 8 }}>{project.display_order}</td>
                <td style={{ border: '1px solid #ddd', padding: 8 }}>{project.published ? 'Yes' : 'No'}</td>
                <td style={{ border: '1px solid #ddd', padding: 8 }}>
                  <button type="button" className="admin-btn admin-btn--ghost" onClick={() => openEdit(project)} style={{ marginRight: 8 }}>Edit</button>
                  <button type="button" className="admin-btn admin-btn--danger" onClick={() => deleteProject(project._id || project.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="admin-panel">
          <h3 className="admin-panel-title">{editingId ? 'Edit Project' : 'Add Project'}</h3>
          <form onSubmit={saveProject}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input placeholder="Title *" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} required />
              <input placeholder="Subtitle (detail page heading)" value={form.subtitle} onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))} />
              <input placeholder="Slug (URL)" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
              <select value={form.column} onChange={(e) => setForm((p) => ({ ...p, column: e.target.value }))}>
                <option value="left">Digital (left column)</option>
                <option value="right">Physical (right column)</option>
              </select>
              <input type="number" placeholder="Display order" value={form.display_order} onChange={(e) => setForm((p) => ({ ...p, display_order: Number(e.target.value) || 0 }))} />
              <select value={form.media_type} onChange={(e) => handleMediaTypeChange(e.target.value)}>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={!!form.published} onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))} />
                Published
              </label>
            </div>

            <div style={{ marginTop: 10 }}>
              <textarea placeholder="Description (shown on project card)" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={3} style={{ width: '100%' }} />
            </div>
            <div style={{ marginTop: 10 }}>
              <textarea placeholder="Body text (detail page, separate paragraphs with blank lines)" value={form.body_text} onChange={(e) => setForm((p) => ({ ...p, body_text: e.target.value }))} rows={3} style={{ width: '100%' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
              <input placeholder="Client" value={form.metadata_client} onChange={(e) => setForm((p) => ({ ...p, metadata_client: e.target.value }))} />
              <input placeholder="Role" value={form.metadata_role} onChange={(e) => setForm((p) => ({ ...p, metadata_role: e.target.value }))} />
              <input placeholder="Year / Time" value={form.metadata_year} onChange={(e) => setForm((p) => ({ ...p, metadata_year: e.target.value }))} />
            </div>

            {form.media_type === 'video' && (
              <div style={{ marginTop: 14, marginBottom: 18 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Video</label>
                <div
                  onDrop={handleVideoDrop}
                  onDragOver={handleVideoDragOver}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  style={{
                    padding: 16,
                    border: '2px dashed #aaa',
                    borderRadius: 6,
                    textAlign: 'center',
                    background: videoUploading ? '#f0f0f0' : '#fafafa'
                  }}
                >
                  <span
                    onClick={() => !videoUploading && videoInputRef.current?.click()}
                    style={{ cursor: videoUploading ? 'wait' : 'pointer', display: 'block', marginBottom: 8 }}
                  >
                    {videoUploading
                      ? `Uploading video… ${Math.round(videoProgress * 100)}%`
                      : form.media_url
                        ? 'Drag & drop a new video to replace it (or click to select)'
                        : 'Drag & drop a video here (or click to select)'}
                  </span>
                  {videoUploading && (
                    <div style={{ height: 6, background: '#e5e5e5', borderRadius: 3, overflow: 'hidden', maxWidth: 320, margin: '0 auto 8px' }}>
                      <div style={{ height: '100%', width: `${Math.round(videoProgress * 100)}%`, background: '#000', transition: 'width 0.2s ease' }} />
                    </div>
                  )}
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>
                    MP4 (H.264) plays in every browser; WebM and MOV are also accepted. Max 100MB. Plays muted and looping when visitors hover over it.
                  </p>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept={VIDEO_ACCEPT}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) handleVideoFile(file);
                    }}
                  />
                  {form.media_url && !videoUploading && (
                    <video
                      key={form.media_url}
                      src={formatMediaUrl(form.media_url)}
                      poster={posterUrl ? formatMediaUrl(posterUrl) : undefined}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      style={{ display: 'block', maxWidth: 320, width: '100%', margin: '8px auto 0', background: '#000' }}
                    />
                  )}
                </div>
                <input
                  placeholder="…or paste a direct video link (.mp4 / .webm)"
                  value={form.media_url}
                  onChange={(e) => setForm((p) => ({ ...p, media_url: e.target.value }))}
                  style={{ width: '100%', marginTop: 8 }}
                />
              </div>
            )}

            {form.media_type === 'video' && (
              <AdminImageDropzone
                label="Still image (shown when not hovering)"
                images={posterImages}
                onImagesChange={handlePosterChange}
                onError={setError}
                multiple={false}
                showMainControls={false}
                helpText="Created automatically from your uploaded video. Drop your own image here to replace it."
              />
            )}

            {form.media_type !== 'video' && (
              <AdminImageDropzone
                label="Main image"
                images={mainImages}
                onImagesChange={handleMainImageChange}
                onError={setError}
                multiple={false}
                showMainControls={false}
                helpText="Shown on the project card and as the featured image on the detail page."
              />
            )}

            <AdminImageDropzone
              label="Extra gallery images"
              images={galleryImages}
              onImagesChange={handleGalleryImagesChange}
              onError={setError}
              showMainControls={false}
              helpText="Optional additional images for the detail page. Drag thumbnails to reorder."
            />

            <div style={{ marginTop: 10 }}>
              <input placeholder="View more button label (default: View..)" value={form.view_more_label} onChange={(e) => setForm((p) => ({ ...p, view_more_label: e.target.value }))} />
            </div>

            {galleryItemsForMetadata.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 12 }}>
                <h4 style={{ marginTop: 0 }}>Gallery image details</h4>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#666' }}>
                  Override title, client, or year for each extra image. Leave a field blank to use the project&apos;s {galleryDefaults.title ? `"${galleryDefaults.title}"` : 'title'}, {galleryDefaults.client ? `"${galleryDefaults.client}"` : 'client'}, and {galleryDefaults.year ? `"${galleryDefaults.year}"` : 'year'}.
                </p>
                {galleryItemsForMetadata.map((item, index) => (
                  <div key={`gallery-meta-${item.image_url || index}`} style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12, marginBottom: 10, background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                      {item.image_url ? (
                        <img src={formatMediaUrl(item.image_url)} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                      ) : null}
                      <div>
                        <strong style={{ fontSize: 13, display: 'block' }}>Extra image {index + 1}</strong>
                        <span style={{ fontSize: 12, color: '#666' }}>
                          Live preview: {resolveGalleryField(item, 'title', form) || 'Untitled'}
                          {resolveGalleryField(item, 'client', form) ? ` · ${resolveGalleryField(item, 'client', form)}` : ''}
                          {resolveGalleryField(item, 'year', form) ? ` · ${resolveGalleryField(item, 'year', form)}` : ''}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                      <div>
                        <input
                          placeholder={galleryDefaults.title || 'Title'}
                          value={item.title || ''}
                          onChange={(e) => updateGalleryItem(index, 'title', e.target.value)}
                        />
                        {isUsingProjectDefault(item, 'title', form) && galleryDefaults.title && (
                          <small style={{ color: '#666' }}>Uses project title</small>
                        )}
                      </div>
                      <div>
                        <input
                          placeholder={galleryDefaults.client || 'Client'}
                          value={item.client || ''}
                          onChange={(e) => updateGalleryItem(index, 'client', e.target.value)}
                        />
                        {isUsingProjectDefault(item, 'client', form) && galleryDefaults.client && (
                          <small style={{ color: '#666' }}>Uses project client</small>
                        )}
                      </div>
                      <div>
                        <input
                          placeholder={galleryDefaults.year || 'Year / Time'}
                          value={item.year || ''}
                          onChange={(e) => updateGalleryItem(index, 'year', e.target.value)}
                        />
                        {isUsingProjectDefault(item, 'year', form) && galleryDefaults.year && (
                          <small style={{ color: '#666' }}>Uses project year</small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="admin-modal-actions">
              <button type="button" className="admin-btn admin-btn--ghost" onClick={() => { setShowForm(false); setEditingId(null); setForm({ ...emptyProject, gallery_items: [] }); }}>Cancel</button>
              <button type="submit" className="admin-btn" disabled={videoUploading}>{editingId ? 'Update Project' : 'Create Project'}</button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-panel">
        <h3 className="admin-panel-title">Portfolio CV / Information</h3>
        <PortfolioCvEditor cv={cv} onChange={setCv} />
        <div className="admin-toolbar" style={{ borderBottom: 'none', marginBottom: 0 }}>
          <button type="button" className="admin-btn" onClick={saveCv} disabled={cvSaving}>
            {cvSaving ? 'Saving…' : 'Save CV'}
          </button>
        </div>
      </div>
    </>
  );
}
